import { forwardRef } from "react";
import { toEnglishDigits } from "@/utils/toEnglishDigits";

/**
 * Strip everything except digits (and one decimal point if allowDecimal).
 * Also converts Arabic-Indic (٠-٩) and Persian (۰-۹) digits → Latin.
 */
export function sanitizeNumericInput(value, { allowDecimal = false } = {}) {
  const latin = toEnglishDigits(value);
  if (allowDecimal) {
    // Allow a single decimal point, drop the rest.
    const cleaned = latin.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length <= 1) return cleaned;
    return parts[0] + "." + parts.slice(1).join("");
  }
  return latin.replace(/[^0-9]/g, "");
}

/**
 * Manual-entry numeric input:
 *   • Latin digits only (auto-converts Arabic-Indic / Persian on the fly).
 *   • type="text" + inputMode="numeric|decimal" — avoids the browser spinner
 *     and lets users type freely on any keyboard, incl. Arabic keyboards.
 *   • Supports optional min/max clamping on blur (never blocks typing).
 *   • dir="ltr" inside the input so numbers align left → right,
 *     regardless of the surrounding RTL layout.
 *
 * Usage:
 *   <NumberField value={price} onChange={setPrice} allowDecimal min={0} max={99999} />
 *   <NumberField value={pax}   onChange={setPax}   min={1} max={60} placeholder="عدد الركاب" />
 */
const NumberField = forwardRef(function NumberField(
  {
    value,
    onChange,
    allowDecimal = false,
    min,
    max,
    placeholder,
    className = "",
    testId,
    disabled,
    ...rest
  },
  ref,
) {
  const handleChange = (e) => {
    const clean = sanitizeNumericInput(e.target.value, { allowDecimal });
    onChange?.(clean, e);
  };

  const handleBlur = (e) => {
    if (value === "" || value == null) return rest.onBlur?.(e);
    const n = Number(value);
    if (Number.isNaN(n)) return rest.onBlur?.(e);
    let clamped = n;
    if (typeof min === "number" && n < min) clamped = min;
    if (typeof max === "number" && n > max) clamped = max;
    if (clamped !== n) onChange?.(String(clamped), e);
    rest.onBlur?.(e);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      dir="ltr"
      value={value ?? ""}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testId}
      autoComplete="off"
      className={className}
      {...rest}
    />
  );
});

export default NumberField;
